import type { AxiosError, AxiosResponse } from 'axios';
import type { CoreApiError } from '@lqa-frontend/api-services';
import { useQuery } from '@tanstack/react-query';
import {
	coreApiClient,
	coreApiQueryKey,
	endpoints,
} from '@lqa-frontend/api-services';

/** Who the partner's flight is billed to. */
export enum PartnerAirfarePaidBy {
	Client = 'client',
	LQA = 'LQA',
	Consultant = 'consultant',
}

/**
 * The airfare rules of one assessment's contract, as the roster needs
 * them to decide whether a flight-cost icon is filled.
 *
 * `null` means the API did not say, which is not the same as `false`: an
 * assessment the request did not cover, or a field it does not return,
 * must not be drawn as though the answer were known.
 */
export interface AssessmentAirfare {
	/** `acmd.id` — matches `assessmentId` on a roster day. */
	assessmentId: number;
	partnerAirfarePaidBy: PartnerAirfarePaidBy | null;
	/**
	 * Whether the contract includes the consultant's airfare.
	 *
	 * Always `null` today: the endpoint's query joins `contract` but does
	 * not select `consultantAirfareIncluded`, so no route the roster can
	 * call returns it. The legacy roster reads the same missing field and
	 * so draws every consultant plane filled —
	 * `admin-frontend/src/screens/scheduling/roster/RosterIconHelpers.ts`
	 * treats `undefined` as "not included". Adding the column to the
	 * select in `assessmentContractManagementDetails.repository.ts`
	 * (`getAssessmentsByUserId`) is all that is needed to light this up.
	 */
	consultantAirfareIncluded: boolean | null;
}

export interface AssessmentAirfareParams {
	consultantId: number;
	month: number;
	year: number;
}

/**
 * One row of `GET /flight-planner/getAssessment/{id}/{month}/{year}`.
 *
 * The route answers with the rows of a hand-written query rather than a
 * serialized DTO, so the fields are read defensively: the shape is the
 * SQL select list, which no DTO pins down.
 */
interface IRawAssessmentRow {
	id?: number | string;
	partnerAirfarePaidBy?: unknown;
	consultantAirfareIncluded?: unknown;
}

function toPartnerAirfarePaidBy(value: unknown): PartnerAirfarePaidBy | null {
	const payers: PartnerAirfarePaidBy[] = [
		PartnerAirfarePaidBy.Client,
		PartnerAirfarePaidBy.LQA,
		PartnerAirfarePaidBy.Consultant,
	];
	return payers.find((payer) => payer === value) ?? null;
}

function toOptionalBoolean(value: unknown): boolean | null {
	return typeof value === 'boolean' ? value : null;
}

function toAssessmentAirfares(rows: IRawAssessmentRow[]): AssessmentAirfare[] {
	const airfares: AssessmentAirfare[] = [];

	for (const row of rows) {
		const assessmentId = Number(row.id);
		if (!Number.isFinite(assessmentId) || assessmentId === 0) continue;

		airfares.push({
			assessmentId,
			partnerAirfarePaidBy: toPartnerAirfarePaidBy(row.partnerAirfarePaidBy),
			consultantAirfareIncluded: toOptionalBoolean(
				row.consultantAirfareIncluded
			),
		});
	}

	return airfares;
}

/**
 * The airfare rules for every assessment a consultant is booked on in a
 * month — one request per open roster, rather than one per trip.
 *
 * Two caveats come from the endpoint's own query, and both end in an
 * assessment simply being absent (so its icons stay outline):
 *
 * - it returns nothing unless that month's `schedule_status` is
 *   `published`;
 * - it matches on the assessment's `boardDate` month, so an assessment
 *   that starts in the previous month is not described.
 *
 * Cached for five minutes: contract rules do not change while an admin
 * reads a roster, and the drawer re-opens often.
 */
export function useAssessmentAirfareQuery(
	params: AssessmentAirfareParams | null
) {
	return useQuery<
		AxiosResponse<IRawAssessmentRow[]>,
		AxiosError<CoreApiError>,
		AssessmentAirfare[]
	>({
		queryKey: coreApiQueryKey(
			endpoints.flightPlanner.monthAssessments(0, 0, 0),
			'get',
			params
		),
		queryFn: async () =>
			coreApiClient.get<IRawAssessmentRow[]>(
				endpoints.flightPlanner.monthAssessments(
					params!.consultantId,
					params!.month,
					params!.year
				)
			),
		enabled: params != null,
		staleTime: 5 * 60 * 1000,
		select: (response) => toAssessmentAirfares(response.data ?? []),
	});
}
